import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, MessageSquare, User, Shield } from 'lucide-react';
import { JewelButton } from '../ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

/**
 * iMessage-style threaded conversation for an HR notice.
 * Both parties (recipient + manager) can post unlimited follow-up messages.
 */
export const NoticeThread = ({ notice, currentUser, onUpdate }) => {
  const [thread, setThread] = useState(notice.thread || []);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const currentUsername = currentUser?.username;
  const iAmRecipient = notice.staff_username === currentUsername;

  // Poll the thread every 15s so both sides see replies land near-real-time.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/hr-notices/notice/${notice.id}/thread`, { headers: h });
        if (alive) setThread(r.data.thread || []);
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [notice.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    const h = getAuthHeaders();
    try {
      const r = await axios.post(`${BACKEND_URL}/api/hr-notices/notice/${notice.id}/message`, { text }, { headers: h });
      setThread(prev => [...prev, r.data.message]);
      setInput('');
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
    setBusy(false);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden" data-testid="hr-thread">
      <div className="px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.02] flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5 text-cyan-300" />
        <p className="text-white/85 text-xs font-semibold tracking-[0.1em] uppercase">Conversation</p>
        <span className="ml-auto text-white/40 text-[10px]">{thread.length} {thread.length === 1 ? 'message' : 'messages'}</span>
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto jewel-scroll px-4 py-3 space-y-2.5">
        {thread.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-white/40 text-xs">No replies yet — start the conversation.</p>
          </div>
        ) : thread.map((m) => {
          const mine = m.sender === currentUsername;
          const isManager = m.is_manager || ['manager', 'admin', 'executive', 'superadmin'].includes(m.sender_role);
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`hr-thread-msg-${m.id}`}>
              <div className={`flex items-end gap-2 max-w-[80%] ${mine ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${isManager ? 'bg-violet-500/20 border-violet-500/40 text-violet-200' : 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200'}`}>
                  {isManager ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                </div>
                <div className={`rounded-2xl px-3.5 py-2 ${mine ? 'bg-gradient-to-br from-cyan-500/25 to-violet-500/20 border border-cyan-400/30' : 'bg-white/[0.04] border border-white/[0.06]'}`}>
                  <p className="text-[10px] text-white/45 mb-0.5 flex items-center gap-1.5">
                    <span className="font-medium text-white/70">{m.sender_name}</span>
                    <span>·</span>
                    <span>{new Date(m.sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </p>
                  <p className="text-white/90 text-[13px] leading-relaxed whitespace-pre-wrap">{m.text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-3 py-2.5 border-t border-white/[0.06] bg-black/20">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
            data-testid="hr-thread-input"
            className="flex-1 px-3 py-2 bg-black/40 border border-white/[0.06] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40 resize-none min-h-[38px] max-h-32"
          />
          <JewelButton accent="cyan" onClick={send} loading={busy} disabled={busy || !input.trim()} icon={Send} data-testid="hr-thread-send">
            Send
          </JewelButton>
        </div>
      </div>
    </div>
  );
};

export default NoticeThread;
